import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

const MAX_POLLS = 10;
const POLL_MS = 2000;

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [packs, setPacks] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let cancelled = false;
    let attempts = 0;

    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid" && data.credited) {
          setPacks(data.packs || 0);
          setState("paid");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState("error");
          return;
        }
      } catch {
        if (cancelled) return;
      }
      attempts += 1;
      if (attempts >= MAX_POLLS) {
        if (!cancelled) setState("pending");
        return;
      }
      setTimeout(poll, POLL_MS);
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#fdf6ec] flex items-center justify-center p-6" data-testid="payment-success-page">
      <div className="bg-white rounded-3xl border border-[#f0e4d4] p-8 max-w-md w-full flex flex-col items-center gap-4 text-center shadow-[0_12px_40px_rgba(232,131,107,0.12)]">
        {state === "checking" && (
          <>
            <Loader2 size={48} className="text-[#e8836b] animate-spin" />
            <h1 data-testid="payment-checking" className="text-xl font-extrabold text-[#2b2622]">
              Verifica del pagamento…
            </h1>
            <p className="text-sm text-[#7a6f64]">Confermiamo il pagamento con Stripe. Attendi qualche istante.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 size={48} className="text-[#2e9e6b]" />
            <h1 data-testid="payment-paid" className="text-xl font-extrabold text-[#2b2622]">
              Pagamento confermato!
            </h1>
            <p className="text-sm text-[#7a6f64]">
              {packs > 0
                ? `${packs} ${packs === 1 ? "pacchetto accreditato" : "pacchetti accreditati"} al tuo account.`
                : "Pacchetti accreditati al tuo account."}
            </p>
            <Button
              data-testid="go-to-album-btn"
              onClick={() => navigate("/album")}
              className="w-full bg-[#e8836b] hover:bg-[#d76f57] text-white rounded-full py-6 font-bold"
            >
              Vai al mio album <ArrowRight size={18} className="ml-2" />
            </Button>
          </>
        )}
        {state === "pending" && (
          <>
            <Loader2 size={48} className="text-[#e8836b]" />
            <h1 data-testid="payment-pending" className="text-xl font-extrabold text-[#2b2622]">
              Pagamento in elaborazione
            </h1>
            <p className="text-sm text-[#7a6f64]">
              Stiamo ancora attendendo la conferma. I pacchetti verranno accreditati appena il pagamento risulta completato.
            </p>
            <Button
              onClick={() => navigate("/album")}
              className="w-full bg-[#2b2622] hover:bg-black text-white rounded-full py-6 font-bold"
            >
              Vai al mio album
            </Button>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle size={48} className="text-[#c0392b]" />
            <h1 data-testid="payment-error" className="text-xl font-extrabold text-[#2b2622]">
              Pagamento non riuscito
            </h1>
            <p className="text-sm text-[#7a6f64]">Nessun pacchetto è stato accreditato. Puoi riprovare dalla Home.</p>
            <Button
              onClick={() => navigate("/")}
              className="w-full bg-[#e8836b] hover:bg-[#d76f57] text-white rounded-full py-6 font-bold"
            >
              Torna alla Home
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
